/**
 * 벽돌 텍스트 배치 순수 계산 — Phaser 비의존, 단위테스트 가능.
 * 블록 중심(y)과 높이(height)만으로 HP 라벨 / 보조 글리프 rest Y를 결정.
 */

/**
 * HP 라벨 rest Y — 모든 벽돌에서 중앙. 미스터리 블록도 HP 숫자만 중앙에 표시한다.
 * @param y 블록 중심 Y (월드 좌표)
 * @param height 블록 픽셀 높이
 * @param isMystery 레거시 호출부 호환용. 현재 라벨 위치에는 영향을 주지 않는다.
 * @returns HP 라벨의 rest Y (월드 좌표)
 */
export function brickLabelRestY(y: number, height: number, isMystery: boolean): number {
  void height;
  void isMystery;
  return y;
}

/**
 * "?" 글리프 rest Y — 미스터리 블록 상단 24%에 배치 (HP 라벨과 대칭).
 * @param y 블록 중심 Y (월드 좌표)
 * @param height 블록 픽셀 높이
 * @returns "?" 글리프의 rest Y (월드 좌표)
 */
export function brickGlyphRestY(y: number, height: number): number {
  return y - height * 0.24;
}

export function resolveBrickLabelText(kind: string, hpText: string, shieldHits: number): string {
  if (kind === "shield" && shieldHits > 0) return String(shieldHits);
  return hpText;
}

/** 만피(full HP)일 때 HP 라벨을 숨기고, 피해를 받은 후에만 표시한다. */
export function shouldShowHpLabel(showHpLabel: boolean, isFullHp: boolean): boolean {
  return showHpLabel && !isFullHp;
}

export interface ShieldBadgeLayout {
  readonly labelOffsetX: number;
  readonly iconOffsetX: number;
  readonly iconSize: number;
  readonly labelMaxWidth: number;
}

/**
 * 방어막 벽돌 라벨 배치 — 방패 아이콘과 남은 횟수 숫자를 분리해 HP 숫자로 오독되지 않게 한다.
 */
export function resolveShieldBadgeLayout(width: number, height: number, active: boolean): ShieldBadgeLayout {
  if (!active) {
    return { labelOffsetX: 0, iconOffsetX: 0, iconSize: 0, labelMaxWidth: width * 0.92 };
  }
  const iconSize = Math.max(8, Math.min(width, height) * 0.28);
  return {
    labelOffsetX: iconSize * 0.52,
    iconOffsetX: -iconSize * 0.64,
    iconSize,
    labelMaxWidth: Math.max(8, width * 0.46),
  };
}

/** 벽돌 Y 앵커 묶음 — 펼치기 게임뷰 오프셋 평행이동 단위(시각 위치 + 피격 shake 복귀 기준). */
export interface BrickAnchorsY {
  /** Sprite 시각 Y (월드). */
  y: number;
  /** 피격 shake가 복귀하는 기준 Y(playImpactShake). 항상 y와 동일 오프셋으로 움직여야 한다. */
  restY: number;
  /** HP 라벨 rest Y. */
  labelRestY: number;
  /** "?" 글리프 rest Y. */
  glyphRestY: number;
}

/**
 * 게임뷰 콘텐츠 오프셋 평행이동 — 모든 Y 앵커를 delta만큼 위로(빼기) 옮긴다(delta>0=상향, 접기=음수).
 * 핵심: **y와 restY가 항상 동일 delta로 이동**해야 한다. restY를 함께 옮기지 않으면 펼침 후 첫 피격에
 * playImpactShake가 base(restY)로 스냅해 벽돌이 offset만큼 아래로 떨어지고, 위로 이동한 발사대가
 * 그 내려온 벽돌을 아래로 조준한다(2026-06-14 회귀 — 사용자 "쏘면 블럭이 내려온다").
 */
export function shiftBrickAnchorsY(a: BrickAnchorsY, delta: number): BrickAnchorsY {
  return {
    y: a.y - delta,
    restY: a.restY - delta,
    labelRestY: a.labelRestY - delta,
    glyphRestY: a.glyphRestY - delta,
  };
}

/** 게임플레이상 벽돌 자체가 이동할 때 base/rest 앵커를 같은 방향으로 평행이동한다. */
export function translateBrickAnchorsY(a: BrickAnchorsY, deltaY: number): BrickAnchorsY {
  return {
    y: a.y + deltaY,
    restY: a.restY + deltaY,
    labelRestY: a.labelRestY + deltaY,
    glyphRestY: a.glyphRestY + deltaY,
  };
}

// ── 절대 좌표 reconcile (펼침/접힘 동기 누락 방어 — 2026-06-15) ──────────────
//
// 누적 델타(shiftBrickAnchorsY)는 상태 의존적이라, registry offset(SSoT)과 벽돌 적용값이 한 번
// 갈리면(race) 영구히 어긋나 벽돌이 게임뷰 마스크 밖에 잔존한다(게임 진행 막힘). 아래 함수들은
// "offset 0 기준 base"를 SSoT로 두고 절대 위치(y = base - offset)를 멱등 재계산해, 어떤 race로
// 어긋나도 다음 동기화에 강제 복구한다. 마스크(시각)와 위치(충돌)는 분리 — 위치는 clamp하지 않는다.

/**
 * offset이 이미 반영된 현재 앵커에서 "offset 0 기준 base" 앵커를 역산한다(생성 시 1회).
 * 생성 좌표는 layoutBrickCells가 `- appliedOffset`을 적용한 값이라, base = 현재 + appliedOffset.
 * @param a 현재(offset 반영) 앵커
 * @param appliedOffset 생성 시점에 반영돼 있던 게임뷰 콘텐츠 오프셋
 */
export function deriveContentBaseY(a: BrickAnchorsY, appliedOffset: number): BrickAnchorsY {
  return {
    y: a.y + appliedOffset,
    restY: a.restY + appliedOffset,
    labelRestY: a.labelRestY + appliedOffset,
    glyphRestY: a.glyphRestY + appliedOffset,
  };
}

/**
 * base 앵커에서 offset을 적용한 **절대** 앵커(y = base - offset)를 산출한다. 누적이 아니라 절대라
 * N번 호출해도 결과가 같다(멱등) — reconcile의 핵심. 위치를 마스크/게임뷰 안으로 clamp하지 않는다
 * (큰 offset이면 음수 y도 그대로 — 충돌은 실제 좌표, 시각만 마스크가 클립).
 */
export function resolveAnchorsAtOffset(base: BrickAnchorsY, offset: number): BrickAnchorsY {
  return {
    y: base.y - offset,
    restY: base.restY - offset,
    labelRestY: base.labelRestY - offset,
    glyphRestY: base.glyphRestY - offset,
  };
}

/** 현재 y가 기대 절대위치(baseY - offset)와 eps 이상 어긋났는지(= reconcile 필요). */
export function contentOffsetMismatch(
  currentY: number,
  baseY: number,
  offset: number,
  eps = 0.5,
): boolean {
  return Math.abs(currentY - (baseY - offset)) > eps;
}

/** reconcile 대상 벽돌 샘플 — base(offset 0 기준)와 현재 시각 y. */
export interface BrickOffsetSample {
  baseY: number;
  currentY: number;
}

/**
 * 주어진 offset에서 기대 위치와 어긋난(stale) 벽돌의 인덱스만 반환한다(순수 결정 로직 — 단위테스트 대상).
 * 모두 정합이면 빈 배열 → 보정 후 재호출 시 대상 0(멱등 수렴).
 */
export function resolveReconcilePlan(
  bricks: readonly BrickOffsetSample[],
  offset: number,
  eps = 0.5,
): number[] {
  const out: number[] = [];
  for (let i = 0; i < bricks.length; i++) {
    if (contentOffsetMismatch(bricks[i].currentY, bricks[i].baseY, offset, eps)) out.push(i);
  }
  return out;
}
